import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sparkles, Plus, Trash2, Save, Loader2, X, Eye, AlertTriangle, IndianRupee, Filter,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRITERIA = [
  { key: "clients", label: "Clients", taskField: "client" },
  { key: "main_titles", label: "Main Titles", taskField: "main_title" },
  { key: "seasons", label: "Seasons", taskField: "season_number" },
  { key: "episode_titles", label: "Episode Titles", taskField: "episode_title" },
  { key: "episode_numbers", label: "Episode Numbers", taskField: "episode_number" },
  { key: "task_types", label: "Task Types", taskField: "task" },
  { key: "languages", label: "Languages", taskField: "language" },
];

const EMPTY_RULE = {
  scope: "default",
  resource_email: null,
  clients: [],
  main_titles: [],
  seasons: [],
  episode_titles: [],
  episode_numbers: [],
  task_types: [],
  languages: [],
  rate: "",
  rate_type: "per_unit",
  note: "",
};

function MultiSelect({ label, value, onChange, options, testid }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const toggle = (opt) => {
    const next = value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt];
    onChange(next);
  };

  const filtered = (options || []).filter((o) => o && o.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-1" ref={ref}>
      <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</Label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full min-h-[36px] px-2 py-1 text-xs border border-slate-200 rounded-md bg-white text-slate-700 hover:border-slate-300 flex items-center gap-1 flex-wrap"
        data-testid={`ms-${testid}`}
      >
        {value.length === 0 ? (
          <span className="text-slate-400">Any {label.toLowerCase()}</span>
        ) : (
          value.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 rounded px-1.5 py-0.5 text-[10px]">
              {v}
              <X className="w-2.5 h-2.5 cursor-pointer" onClick={(e) => { e.stopPropagation(); toggle(v); }} />
            </span>
          ))
        )}
      </button>
      {open && (
        <div className="relative">
          <div className="absolute z-30 top-1 left-0 right-0 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-auto">
            <div className="sticky top-0 bg-white border-b border-slate-100 p-1.5">
              <Input className="h-7 text-xs" placeholder={`Search ${label.toLowerCase()}…`} value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
            </div>
            <div className="px-1 py-1">
              {filtered.length === 0 ? (
                <div className="text-[10px] text-slate-400 px-2 py-1">No options</div>
              ) : filtered.map((opt) => {
                const checked = value.includes(opt);
                return (
                  <label key={opt} className={`flex items-center gap-2 px-2 py-1 text-xs rounded cursor-pointer ${checked ? "bg-blue-50 text-blue-700" : "hover:bg-slate-50 text-slate-700"}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(opt)} className="accent-blue-600" />
                    <span className="truncate">{opt}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdvancedPriceRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ ...EMPTY_RULE });
  const [editingId, setEditingId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState({
    clients: [], main_titles: [], seasons: [], episode_titles: [],
    episode_numbers: [], task_types: [], languages: [], resources: [],
  });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [rulesRes, optRes, resRes] = await Promise.all([
        axios.get(`${API}/resource-center/advanced-rules`),
        axios.get(`${API}/database/distinct-values`).catch(() => null),
        axios.get(`${API}/resource-mappings`).catch(() => ({ data: { mappings: [] } })),
      ]);
      setRules(rulesRes.data.rules || []);
      if (optRes?.data) {
        setOptions((o) => ({
          ...o,
          clients: optRes.data.clients || [],
          main_titles: optRes.data.main_titles || [],
          seasons: (optRes.data.seasons || []).map(String),
          episode_titles: optRes.data.episode_titles || [],
          episode_numbers: (optRes.data.episode_numbers || []).map(String),
          task_types: optRes.data.tasks || [],
          languages: optRes.data.languages || [],
        }));
      }
      setOptions((o) => ({ ...o, resources: (resRes.data.mappings || []).map((m) => m.email) }));
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Live preview (debounced)
  const fetchPreview = useCallback(async (rule) => {
    setPreviewing(true);
    try {
      const res = await axios.post(`${API}/resource-center/advanced-rules/preview`, {
        ...rule,
        rate: parseFloat(rule.rate) || 0,
      });
      setPreview(res.data);
    } catch (e) {
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  }, []);

  useEffect(() => {
    const hasCriteria = CRITERIA.some((c) => (draft[c.key] || []).length > 0);
    if (!hasCriteria || !draft.rate) { setPreview(null); return; }
    const t = setTimeout(() => fetchPreview(draft), 350);
    return () => clearTimeout(t);
  }, [draft, fetchPreview]);

  const updateField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const editRule = (rule) => {
    setDraft({
      ...EMPTY_RULE,
      ...rule,
      rate: String(rule.rate),
      note: rule.note || "",
    });
    setEditingId(rule.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setDraft({ ...EMPTY_RULE });
    setEditingId(null);
    setPreview(null);
  };

  const saveRule = async () => {
    const hasCriteria = CRITERIA.some((c) => (draft[c.key] || []).length > 0);
    if (!hasCriteria) { toast.error("Pick at least one criterion"); return; }
    if (!draft.rate || isNaN(parseFloat(draft.rate))) { toast.error("Enter a valid rate"); return; }
    if (draft.scope === "resource" && !draft.resource_email) { toast.error("Pick a resource"); return; }
    setSaving(true);
    try {
      const payload = { ...draft, rate: parseFloat(draft.rate) };
      if (editingId) {
        await axios.put(`${API}/resource-center/advanced-rules/${editingId}`, payload);
        toast.success("Rule updated");
      } else {
        await axios.post(`${API}/resource-center/advanced-rules`, payload);
        toast.success("Rule created");
      }
      cancelEdit();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (id) => {
    if (!window.confirm("Delete this rule?")) return;
    try {
      await axios.delete(`${API}/resource-center/advanced-rules/${id}`);
      toast.success("Rule deleted");
      fetchAll();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const applyNow = async () => {
    try {
      const res = await axios.post(`${API}/resource-center/apply-rates`);
      toast.success(`Applied — ${res.data.updated} tasks updated`);
    } catch (e) {
      toast.error("Apply failed");
    }
  };

  const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

  return (
    <div className="p-4 sm:p-6 max-w-full overflow-x-hidden" data-testid="advanced-rules-page">
      <div className="flex items-start justify-between mb-4 sm:mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-violet-600" />
            Advanced Price Rules
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Multi-criteria rate rules — match tasks by any combination of client, title, season, episode, task type, or language.
            Advanced rules <strong>override</strong> all other rate tiers.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={applyNow} data-testid="apply-rates-btn">
          <Filter className="w-4 h-4 mr-1.5" /> Apply Rates Now
        </Button>
      </div>

      {/* Draft / Editor card */}
      <Card className="mb-6 border-violet-200 shadow-sm" data-testid="rule-editor">
        <CardHeader className="py-3 bg-violet-50/40">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Plus className="w-4 h-4 text-violet-600" />
            {editingId ? "Edit Rule" : "Create New Rule"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 p-4">
          {/* Scope + Resource picker */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Scope</Label>
              <Select value={draft.scope} onValueChange={(v) => setDraft((d) => ({ ...d, scope: v, resource_email: v === "default" ? null : d.resource_email }))}>
                <SelectTrigger className="h-9" data-testid="scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default (everyone)</SelectItem>
                  <SelectItem value="resource">Per Resource</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {draft.scope === "resource" && (
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Resource</Label>
                <Select value={draft.resource_email || ""} onValueChange={(v) => updateField("resource_email", v)}>
                  <SelectTrigger className="h-9" data-testid="resource-select"><SelectValue placeholder="Pick resource" /></SelectTrigger>
                  <SelectContent>
                    {options.resources.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Rate Type</Label>
              <Select value={draft.rate_type} onValueChange={(v) => updateField("rate_type", v)}>
                <SelectTrigger className="h-9" data-testid="rate-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_unit">Per Unit (× duration)</SelectItem>
                  <SelectItem value="flat">Flat amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Rate (₹)</Label>
              <div className="relative">
                <IndianRupee className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input type="number" step="0.01" className="h-9 pl-7" value={draft.rate} onChange={(e) => updateField("rate", e.target.value)} placeholder="0.00" data-testid="rate-input" />
              </div>
            </div>
          </div>

          {/* Criteria multi-selects */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {CRITERIA.map((c) => (
              <MultiSelect
                key={c.key}
                label={c.label}
                value={draft[c.key] || []}
                onChange={(v) => updateField(c.key, v)}
                options={options[c.key] || []}
                testid={c.key}
              />
            ))}
          </div>

          {/* Note */}
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Note (optional)</Label>
            <Input className="h-9" placeholder="Why this rule exists…" value={draft.note} onChange={(e) => updateField("note", e.target.value)} data-testid="note-input" />
          </div>

          {/* Live preview */}
          {preview && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="rule-preview">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-4 h-4 text-slate-500" />
                <span className="text-xs font-semibold text-slate-700">Live Preview</span>
                {previewing && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
                <span className="ml-auto text-[10px] text-slate-400">Specificity: {preview.specificity}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="bg-white rounded p-2 border border-slate-100">
                  <p className="text-[10px] uppercase text-slate-400">Will match</p>
                  <p className="text-lg font-semibold text-violet-700" data-testid="preview-match-count">{preview.match_count}</p>
                  <p className="text-[10px] text-slate-400">tasks</p>
                </div>
                <div className="bg-white rounded p-2 border border-slate-100">
                  <p className="text-[10px] uppercase text-slate-400">Old total</p>
                  <p className="text-sm font-mono text-slate-700">{fmt(preview.total_old_amount)}</p>
                </div>
                <div className="bg-white rounded p-2 border border-slate-100">
                  <p className="text-[10px] uppercase text-slate-400">New total</p>
                  <p className="text-sm font-mono text-emerald-700 font-semibold">{fmt(preview.total_new_amount)}</p>
                </div>
                <div className={`rounded p-2 border ${Math.abs(preview.amount_delta) > 0.01 ? "bg-amber-50 border-amber-200" : "bg-white border-slate-100"}`}>
                  <p className="text-[10px] uppercase text-slate-400">Delta</p>
                  <p className={`text-sm font-mono font-semibold ${preview.amount_delta > 0 ? "text-emerald-700" : preview.amount_delta < 0 ? "text-red-600" : "text-slate-700"}`}>
                    {preview.amount_delta > 0 ? "+" : ""}{fmt(preview.amount_delta)}
                  </p>
                </div>
              </div>
              {preview.match_count === 0 && (
                <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded p-2 flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3" /> No tasks match these criteria yet. Save the rule anyway if future tasks will match.
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2">
            <Button onClick={saveRule} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="save-rule-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              {editingId ? "Update Rule" : "Save Rule"}
            </Button>
            {editingId && (
              <Button variant="outline" onClick={cancelEdit} data-testid="cancel-edit-btn">Cancel</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Existing rules list */}
      <Card data-testid="rules-list">
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-semibold">
            Existing Rules <span className="text-slate-400 font-normal">({rules.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-10 text-slate-400 text-sm">Loading…</div>
          ) : rules.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">No advanced rules yet. Create one above.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {rules.map((r) => (
                <div key={r.id} className="px-4 py-3 hover:bg-slate-50/60 transition-colors" data-testid={`rule-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <Badge className={r.scope === "resource" ? "bg-blue-100 text-blue-700 border-0 text-[10px]" : "bg-violet-100 text-violet-700 border-0 text-[10px]"}>
                          {r.scope === "resource" ? `${r.resource_email}` : "Default"}
                        </Badge>
                        <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] font-mono">
                          ₹{r.rate} {r.rate_type === "flat" ? "flat" : "/unit"}
                        </Badge>
                        <span className="text-[10px] text-slate-400">specificity: {r._specificity || 0}</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {CRITERIA.map((c) => (r[c.key] || []).length > 0 && (
                          <span key={c.key} className="inline-flex items-center gap-1 text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">
                            <span className="font-semibold">{c.label}:</span> {(r[c.key] || []).slice(0, 3).join(", ")}{(r[c.key] || []).length > 3 ? ` +${r[c.key].length - 3}` : ""}
                          </span>
                        ))}
                      </div>
                      {r.note && <p className="text-[11px] text-slate-500 mt-1 italic">"{r.note}"</p>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" onClick={() => editRule(r)} className="h-7 text-[10px]" data-testid={`edit-${r.id}`}>Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => deleteRule(r.id)} className="h-7 text-[10px] text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-${r.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
